/**
 * Skills: ERP reports run from a form (backend/chainlit/skills_api.py).
 *
 * The catalog and the pickers are plain REST - no socket, so opening them
 * reserves no container. Running a skill sends an ordinary chat message whose
 * metadata carries the form; the chat answers it without the model
 * (signals-copilot docker/skills.py).
 */

export type SkillParamType =
  | 'party'
  | 'account'
  | 'item'
  | 'warehouse'
  | 'pick'
  | 'choice'
  | 'text'
  | 'number'
  | 'date';

/** Types that are typed or chosen in the form, not searched in a list. */
export const NOT_PICKERS: SkillParamType[] = [
  'date',
  'choice',
  'text',
  'number'
];
export const isPicker = (p: SkillParam) => !NOT_PICKERS.includes(p.type);

export interface SkillParam {
  name: string;
  label: string;
  type: SkillParamType;
  required?: boolean;
  /** choice: the fixed options, and which one the form starts on. */
  options?: { value: string; label: string }[];
  default?: string;
}

export interface Skill {
  id: string;
  label: string;
  label_ar?: string;
  group?: string;
  favorite?: boolean;
  description: string;
  params: SkillParam[];
  one_of?: string[];
  max_days?: number;
  max_months?: number;
}

export interface SkillOption {
  value: string;
  label: string;
  hint?: string | null;
  image?: string | null;
}

/** What the form holds per field: the value sent, and the label shown. */
export type SkillValues = Record<
  string,
  { value: string; label: string } | undefined
>;

export async function fetchSkills(): Promise<Skill[]> {
  try {
    const res = await fetch('/api/skills', { credentials: 'include' });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body?.skills) ? body.skills : [];
  } catch {
    return [];
  }
}

export async function fetchSkillOptions(
  skill: string,
  param: string,
  q: string,
  signal?: AbortSignal
): Promise<{ options: SkillOption[]; timedOut: boolean }> {
  const qs = new URLSearchParams({ skill, param, q });
  try {
    const res = await fetch(`/api/skills/options?${qs}`, {
      credentials: 'include',
      signal
    });
    if (!res.ok) return { options: [], timedOut: false };
    const body = await res.json();
    return {
      options: Array.isArray(body?.options) ? body.options : [],
      timedOut: Boolean(body?.timed_out)
    };
  } catch {
    return { options: [], timedOut: false };
  }
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** The form's opening dates: the start of this year to today. */
export function defaultPeriod(today = new Date()): {
  from: string;
  to: string;
} {
  return { from: `${today.getFullYear()}-01-01`, to: isoDay(today) };
}

/** The first thing wrong with the form, in English, or null. The container
 *  checks again; this only saves a round trip. */
export function validateSkill(
  skill: Skill,
  values: SkillValues
): string | null {
  for (const p of skill.params) {
    if (p.required && !values[p.name]?.value)
      return `Choose ${p.label.toLowerCase()}.`;
  }
  if (skill.one_of && !skill.one_of.some((n) => values[n]?.value)) {
    const labels = skill.params
      .filter((p) => skill.one_of!.includes(p.name))
      .map((p) => p.label.toLowerCase());
    return `Choose ${labels.join(' or ')}.`;
  }
  const from = values.date_from?.value;
  const to = values.date_to?.value;
  if (from && to) {
    if (to < from) return 'The period ends before it starts.';
    const days = (Date.parse(to) - Date.parse(from)) / 86_400_000;
    if (skill.max_days && days > skill.max_days)
      return `Choose a period of at most ${skill.max_days} days.`;
    if (skill.max_months) {
      const [fy, fm] = from.split('-').map(Number);
      const [ty, tm] = to.split('-').map(Number);
      if ((ty - fy) * 12 + tm - fm + 1 > skill.max_months)
        return `Choose a period of at most ${skill.max_months} months.`;
    }
  }
  return null;
}

/** The parameters sent, plus the label of each warehouse (the answer names
 *  warehouses by what the user saw, not by their key). */
export function skillParams(
  skill: Skill,
  values: SkillValues
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of skill.params) {
    const v = values[p.name];
    if (!v?.value) continue;
    out[p.name] = v.value;
    // The answer names what the user saw, not the key the picker returned.
    if (isPicker(p)) out[`${p.name}_label`] = v.label.trim();
  }
  return out;
}

/** The line the thread shows for the run: "Stock card · Cocoa 25kg · 2026-07-01 → 2026-09-30". */
export function describeSkillRun(skill: Skill, values: SkillValues): string {
  const picked = skill.params
    .filter((p) => p.type !== 'date' && values[p.name]?.value)
    .map((p) => values[p.name]!.label.trim());
  const from = values.date_from?.value;
  const to = values.date_to?.value;
  return [skill.label, ...picked, from && to ? `${from} → ${to}` : '']
    .filter(Boolean)
    .join(' · ');
}

// ── One shared store: the composer button, the "/" menu and links in answers
// all read the same list, favourites and recent runs. ──────────────────────

let catalog: Skill[] = [];
let loaded = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function subscribeSkills(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const getSkillsSnapshot = () => catalog;

export async function loadSkills(force = false) {
  if (loaded && !force) return catalog;
  catalog = await fetchSkills();
  loaded = true;
  emit();
  return catalog;
}

export async function setSkillFavorite(id: string, on: boolean) {
  // Optimistic: the star moves at once; a failed save puts it back.
  catalog = catalog.map((s) => (s.id === id ? { ...s, favorite: on } : s));
  emit();
  try {
    const res = await fetch(`/api/skills/${encodeURIComponent(id)}/favorite`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ on })
    });
    if (!res.ok) throw new Error(String(res.status));
  } catch {
    catalog = catalog.map((s) => (s.id === id ? { ...s, favorite: !on } : s));
    emit();
  }
}

const RECENT_KEY = 'signals.skills.recent';
const MAX_RECENT = 3;

/** Recently run skills: a per-browser convenience, never relied on. */
export function recentSkillIds(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function noteSkillRun(id: string) {
  try {
    const next = [id, ...recentSkillIds().filter((x) => x !== id)].slice(
      0,
      MAX_RECENT
    );
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* private window: no recents */
  }
}

const FOLD: Record<string, string> = { أ: 'ا', إ: 'ا', آ: 'ا', ى: 'ي', ة: 'ه' };
export const foldText = (t: string) =>
  (t || '').toLowerCase().replace(/[أإآىة]/g, (c) => FOLD[c] || c);

/** How well a skill matches what was typed: 3 when every word starts a word
 *  of its English or Arabic name, 2 when every word is inside the name, 1 when
 *  only the group or description has it (and the query is long enough to
 *  mean it), 0 for no match. */
export function skillScore(skill: Skill, query: string): number {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (!words.length) return 1;
  const name = foldText(`${skill.label} ${skill.label_ar || ''}`);
  const nameWords = name.split(/\s+/);
  if (words.every((w) => nameWords.some((n) => n.startsWith(w)))) return 3;
  if (words.every((w) => name.includes(w))) return 2;
  const rest = foldText(`${skill.group || ''} ${skill.description}`);
  if (
    query.trim().length >= 4 &&
    words.every((w) => name.includes(w) || rest.includes(w))
  )
    return 1;
  return 0;
}

export const matchSkill = (skill: Skill, query: string) =>
  skillScore(skill, query) > 0;

/** The matches, best first; ties keep catalog order. */
export function rankSkills(skills: Skill[], query: string): Skill[] {
  return skills
    .map((s, i) => ({ s, i, score: skillScore(s, query) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .map((x) => x.s);
}

// ── Opening a skill from anywhere ───────────────────────────────────────────

export const OPEN_SKILLS_PALETTE = 'signals-open-skills';
export const OPEN_SKILL = 'signals-open-skill';

export const openSkillsPalette = () =>
  window.dispatchEvent(new CustomEvent(OPEN_SKILLS_PALETTE));

export const openSkill = (id: string, values?: SkillValues) =>
  window.dispatchEvent(new CustomEvent(OPEN_SKILL, { detail: { id, values } }));

/** "#skill=stock_card&item=1201301&item_label=Cocoa&date_from=..." - the link
 *  an answer carries to open the same report as a skill (signals-copilot
 *  server/skill_requests.offer_link). */
export function parseSkillLink(
  href: string
): { id: string; values: SkillValues } | null {
  if (!href || !href.startsWith('#skill=')) return null;
  const params = new URLSearchParams(href.slice(1));
  const id = params.get('skill');
  if (!id) return null;
  const values: SkillValues = {};
  params.forEach((value, key) => {
    if (key === 'skill' || key.endsWith('_label') || !value) return;
    values[key] = { value, label: params.get(`${key}_label`) || value };
  });
  return { id, values };
}
